import { TranslateService } from '@ngx-translate/core';
import { Column, ColumnApi, GridApi, GridOptions, RowNode } from 'ag-grid-community';
import { concat, get, includes, some, uniq } from 'lodash';
import moment from 'moment';

import {
    DynamicFieldOptionsModel,
    SetXcatDynamicColFunctionModel,
} from '@ag-grid-reports/helpers/dynamicFieldOptions.model';
import {
    BUSINESS_DATE_FORMAT,
    DATE_FORMAT,
    DATE_TIME_DEFAULT_FORMAT,
    EN_LOCALE,
    MOMENT_FORMATS,
} from '@config/global-constants';
import { NotificationService, PreferencesService } from '@services/index';
import { ColumnFieldClass } from '@services/preferences/preferences.enum';
import { IPdfReportOptions } from '@services/reports/models/pdf-report-options.model';
import { CustomQueryParameter, HiddenColumns, IEvent } from '@shared/types';
import { AgGridColumnDef } from '@shared/types/grid/ag-grid-column-def';
import { convertUTCDateTimeByTimeZone, findLastIndex, rowLimit } from '@shared/utils';

export class AgGridUtils {
    private static dateFormat = ['YYYY-MM-DD', 'YYYY'];

    constructor() {}

    public static preparePdfSettings(
        preferences: PreferencesService,
        translations: TranslateService,
        gridColumnApi: ColumnApi,
        reportTitle: string,
        reportId: string,
        companyName: string,
        dateTo: string,
        dateFrom: string,
        count: { type: string; value: number },
        subTitles: Array<string | { text: string; class: string; alignment: string }>,
        groupIncludeTotalFooter: boolean,
        printOnlyVisibleRows: boolean,
        keepPinnedForPdfExport: boolean,
        hasChart: boolean,
    ): IPdfReportOptions {
        let exportFileName = reportTitle;
        if (!keepPinnedForPdfExport) {
            gridColumnApi.getDisplayedLeftColumns().forEach((column: any) => {
                gridColumnApi.setColumnPinned(<any>column.colId, null);
            });

            gridColumnApi.getDisplayedRightColumns().forEach((column: any) => {
                gridColumnApi.setColumnPinned(column.colId, null);
            });
        }

        if (hasChart) {
            exportFileName += '_Grid';
        }

        if (dateFrom && dateTo) {
            exportFileName += `_${AgGridUtils.businessDateFormatter(dateFrom, dateTo, preferences, translations, 'YYYY-MM-DD')}`;
        }

        return {
            reportTitle,
            reportId,
            companyName,
            dateTo,
            dateFrom,
            count,
            subTitles,
            groupIncludeTotalFooter,
            printOnlyVisibleRows,
            exportFileName,
        };
    }

    public static businessDateFormatter(
        dateStart: string,
        dateEnd: string,
        preferencesService: PreferencesService,
        translations: TranslateService,
        format = 'L',
        locale?: string,
    ): string {
        if (!dateStart) {
            dateStart = '';
        } else {
            dateStart = dateStart === '(None)' ? '(None)' : dateStart;
        }

        if (!dateEnd) {
            dateEnd = dateStart;
        } else {
            dateEnd = dateStart === '(None)' ? '(None)' : dateEnd;
        }

        const timeLocale = locale ?? preferencesService.timeLocale;

        return dateStart === dateEnd
            ? moment(dateStart, this.dateFormat).locale(timeLocale).format(format)
            : `${moment(dateStart, this.dateFormat).locale(timeLocale).format(format)} - ${moment(dateEnd, this.dateFormat).locale(timeLocale).format(format)}`;
    }

    public static dateFormatter(
        dateStart: string,
        dateEnd: string,
        preferencesService: PreferencesService,
        translateService: TranslateService,
        format?: string,
        withTimeZone = true,
        locale?: string,
    ) {
        if (!dateStart) {
            dateStart = '';
        } else {
            dateStart = dateStart === '(None)' ? '(None)' : dateStart;
        }

        if (!dateEnd) {
            dateEnd = dateStart;
        } else {
            dateEnd = dateStart === '(None)' ? '(None)' : dateEnd;
        }
        return this.areEqualDates(dateStart, dateEnd)
            ? preferencesService.dateFormatter(dateEnd, translateService, format, withTimeZone, locale)
            : `${preferencesService.dateFormatter(dateStart, translateService, format, withTimeZone, locale)} - ${preferencesService.dateFormatter(dateEnd, translateService, format, withTimeZone, locale)}`;
    }

    private static areEqualDates(dateStart: string, dateEnd: string) {
        if (dateStart === '(None)' || dateEnd === '(None)') {
            return false;
        }

        return moment(dateStart, DATE_FORMAT).isSame(moment(dateEnd, DATE_FORMAT), 'day');
    }

    public static handleDynamicAgGridColumnInsertion = (columns, gridColumnApi, prefix, dynamicGroupIndex) => {
        setTimeout(() => {
            let firstDynamicColumnIndex = columns.findIndex((item) => (item.colId || item.field).startsWith(prefix));
            firstDynamicColumnIndex = firstDynamicColumnIndex > -1 ? firstDynamicColumnIndex : dynamicGroupIndex;
            let lastDynamicColumnIndex = findLastIndex(columns, (item: any) =>
                (item.colId || item.field).startsWith(prefix),
            );
            lastDynamicColumnIndex = lastDynamicColumnIndex > -1 ? lastDynamicColumnIndex : dynamicGroupIndex;
            const dynamicColumnsSubStateFromGrid = gridColumnApi
                .getColumnState()
                .filter((item) => (item.colId || item.field).startsWith(prefix));
            const dynamicColumnsSubStateFromBrowser = columns.filter((item) =>
                (item.colId || item.field).startsWith(prefix),
            );
            let filteredDynamicColumnsSubStateFromBrowser = [];

            if (dynamicColumnsSubStateFromBrowser.length > dynamicColumnsSubStateFromGrid.length) {
                filteredDynamicColumnsSubStateFromBrowser = dynamicColumnsSubStateFromBrowser.filter((item) =>
                    dynamicColumnsSubStateFromGrid.some((i) => i.colId === (item.colId || item.field)),
                );
                filteredDynamicColumnsSubStateFromBrowser = [
                    ...filteredDynamicColumnsSubStateFromBrowser,
                    ...dynamicColumnsSubStateFromGrid.filter(
                        (item) =>
                            !filteredDynamicColumnsSubStateFromBrowser.some(
                                (i) => i.colId === (item.colId || item.field),
                            ),
                    ),
                ];
                columns.splice(
                    firstDynamicColumnIndex,
                    lastDynamicColumnIndex - firstDynamicColumnIndex,
                    ...filteredDynamicColumnsSubStateFromBrowser,
                );
            } else {
                filteredDynamicColumnsSubStateFromBrowser = dynamicColumnsSubStateFromGrid.filter(
                    (item) => !dynamicColumnsSubStateFromBrowser.some((i) => i.colId === (item.colId || item.field)),
                );
                columns.splice(lastDynamicColumnIndex, 0, ...filteredDynamicColumnsSubStateFromBrowser);
            }
        });
    };

    public static setupDynamicColumnDefs(
        dynamic_fields: Array<{ name: string; named_code?: string; data_type?: string; config?: object } | string>,
        columnDefs: Array<any>,
        preferencesService: PreferencesService,
        options: DynamicFieldOptionsModel,
    ): { columnDefs: Array<AgGridColumnDef>; dynamicColumns: Array<AgGridColumnDef> } {
        const dynamicColumns: Array<AgGridColumnDef> = [];
        dynamic_fields?.forEach((dynamic_field) => {
            const headerName =
                typeof dynamic_field === 'string'
                    ? options.header_prefix
                        ? options.header_prefix + dynamic_field
                        : dynamic_field
                    : options.header_prefix
                      ? options.header_prefix + dynamic_field.name
                      : dynamic_field.name;
            const field =
                options.field ??
                options.field_prefix +
                    (typeof dynamic_field === 'string' ? dynamic_field : dynamic_field.named_code || dynamic_field.name)
                        .toLowerCase()
                        .split(' ')
                        .join('_')
                        .replace(/-/g, '_') +
                    (options.field_postfix ?? '');
            const dataType =
                options.defaultDataType || (typeof dynamic_field === 'string' ? 'amount' : dynamic_field.data_type);
            const colDef = {
                headerName: headerName,
                field,
                suppressMenuPinSubMenu: true,
                minWidth: options.minWidth || 100,
                cellRenderer:
                    options.cellRenderer ||
                    (options.valueFormatter ? null : this.getCellRendererByDataType(dataType, preferencesService)),
                comparator: options.comparator,
                cellClass: [`${this.getCellClassByDataType(dataType)}`],
                filter: options.filter,
                filterParams:
                    options.filterParams ||
                    options.filterParamsFabric({
                        colType: this.getCellClassByDataType(dataType),
                        renderer: this.getCellRendererByDataType(dataType, preferencesService),
                    }) ||
                    null,
                enableRowGroup: options.enableRowGroup ?? false,
                rowGroup: options.rowGroup ?? false,
                rowGroupIndex: options.rowGroup ? (options.rowGroupIndex ?? null) : null,
                hide: options.hideByDefault ?? false,
                suppressColumnsToolPanel: options.suppressColumnsToolPanel,
                ...(options.valueFormatter ? { valueFormatter: options.valueFormatter } : {}),
                ...(typeof dynamic_field === 'string' ? {} : dynamic_field.config || {}),
            };

            if (options.valueGetter) {
                colDef['valueGetter'] = (params) =>
                    typeof options.valueGetter === 'string' ? options.valueGetter : options.valueGetter(params);
            }
            if (options.comparator) {
                colDef['comparator'] = options.comparator;
            }
            if (dataType !== 'string') {
                colDef['aggFunc'] = options.aggFunc ?? 'customSum';
            }
            if (options.sort) {
                colDef['sort'] = options.sort;
                if (options.sortIndex) {
                    colDef['sortIndex'] = options.sortIndex;
                }
            }
            dynamicColumns.push(colDef);
        });
        return this.insertDynamicColumns(dynamicColumns, options.insert_field, columnDefs, options.alwaysInsert);
    }

    private static insertDynamicColumns(
        dynamicColumns: any,
        field_to_add_to: string,
        columnDefs: Array<any>,
        alwaysInsert: boolean,
    ): { columnDefs: Array<AgGridColumnDef>; dynamicColumns: Array<AgGridColumnDef> } {
        const index = columnDefs.findIndex((col) => col.field === field_to_add_to);

        if (index === -1 && !alwaysInsert) {
            return { columnDefs, dynamicColumns };
        }
        columnDefs.splice(index + 1, 0, ...dynamicColumns);

        return { columnDefs, dynamicColumns };
    }

    public static setupDynamicGroup({
        group_header,
        group_key,
        group_children_options,
        columnDefs,
        aggFunc,
        cellRenderer,
        cellClass,
        marryChildren,
        insertField,
    }): { columnDefs: Array<AgGridColumnDef>; dynamicColumns: Array<AgGridColumnDef> } {
        const dynamicGroupDef: Array<AgGridColumnDef> = [];
        dynamicGroupDef.push({
            headerName: group_header,
            field: group_key,
            suppressMenuPinSubMenu: true,
            minWidth: 100,
            aggFunc: aggFunc,
            cellRenderer: cellRenderer,
            cellClass: [cellClass],
            enableRowGroup: false,
            marryChildren: marryChildren,
            children: group_children_options.map((options) => {
                const colDef = {
                    headerName: options.headerName,
                    field: options.field,
                    minWidth: 100,
                    aggFunc: options.aggFunc,
                    cellRenderer: options.cellRenderer,
                    cellClass: options.cellClass,
                    filter: options.filter,
                    filterParams: options.filterParams,
                };
                if (options.valueGetter) {
                    colDef['valueGetter'] = options.valueGetter;
                }
                return colDef;
            }),
        });
        return this.insertDynamicColumns(dynamicGroupDef, insertField, columnDefs, true);
    }

    public static setXcatDynamicColumns({
        columnDefs,
        datas,
        prefix,
        preferencesService,
        currencyColumnBaseConfig,
        totalCol,
        translations,
        dataSourceKey,
        customSortItems = [],
        insertField = '',
    }: SetXcatDynamicColFunctionModel): AgGridColumnDef[] {
        const insert_fields: any[] = [];
        const custom_dynamic_keys: Set<string> = new Set();

        const totalColKey = totalCol.colName;
        datas.forEach((dataItem) => {
            const dataSourceString = get(dataItem, dataSourceKey, '');
            const dataSource = JSON.parse(dataSourceString || '[]');
            dataSource.push({ [totalColKey]: dataItem[totalCol.valueField] });

            dataSource.forEach((data) => {
                Object.entries(data).forEach(([key, value]) => {
                    key = key ? key : translations.instant('N/A');
                    custom_dynamic_keys.add(key);
                    dataItem[`${prefix}_${key}`] = value;
                });
            });
        });

        custom_dynamic_keys.delete(totalColKey);
        const sortedKeys = [...custom_dynamic_keys].sort((typeA, typeB) => {
            const indexA = customSortItems.indexOf(typeA);
            const indexB = customSortItems.indexOf(typeB);

            if (indexA !== indexB) {
                return indexA > indexB ? -1 : 1;
            } else {
                const lowercaseA = typeA.toLocaleLowerCase();
                const lowercaseB = typeB.toLocaleLowerCase();
                return lowercaseA === lowercaseB ? 0 : lowercaseA > lowercaseB ? 1 : -1;
            }
        });
        sortedKeys.concat(totalColKey).forEach((type) => {
            insert_fields.push({
                name: type,
                data_type: 'amount',
                config: {
                    headerName: type,
                    field: `${prefix}_${type}`,
                    hide: false,
                    ...currencyColumnBaseConfig,
                },
            });
        });

        if (insert_fields.length) {
            const { dynamicColumns } = AgGridUtils.setupDynamicColumnDefs(
                insert_fields,
                columnDefs,
                preferencesService,
                {
                    insert_field: insertField,
                    field_prefix: prefix,
                    header_prefix: '',
                    defaultDataType: '',
                    alwaysInsert: true,
                    hideByDefault: false,
                    filter: 'agMultiColumnFilter',
                    filterParams: {
                        valueFormatter: ({ value }) => translations.instant(value),
                    },
                },
            );

            datas.forEach((dataItem) => {
                insert_fields.forEach((field) => {
                    dataItem[field.config.field] = dataItem[field.config.field] || 0;
                });
            });

            return dynamicColumns;
        } else {
            return [];
        }
    }

    public static getCellRendererByDataType(
        data_type: string,
        preferencesService: PreferencesService,
    ): (renderParams, args?: any) => string | number {
        switch (data_type) {
            case 'percents':
                return preferencesService.percentsFormatterForAgGrid;
            case 'amount':
                return preferencesService.currencyFormatterForAgGrid;
            case 'number':
                return preferencesService.numberFormatterForAgGrid;
            case 'float':
                return preferencesService.floatFormatterForAgGrid;
            case 'time':
                return (renderParams) => preferencesService.floatFormatterForAgGrid(renderParams, 1);
            case 'dateTime':
                return preferencesService.dateTimeFormatterForAgGrid;
            case 'string':
                return preferencesService.stringFormatterForAgGrid;
            case 'duration':
                return preferencesService.formatToDuration;
            default:
                return preferencesService.currencyFormatterForAgGrid;
        }
    }

    public static getCellClassByDataType(data_type: string): ColumnFieldClass {
        switch (data_type) {
            case 'percents':
                return ColumnFieldClass.CUSTOM_PERCENTS;
            case 'amount':
                return ColumnFieldClass.CUSTOM_PRICE;
            case 'number':
                return ColumnFieldClass.CUSTOM_COUNT;
            case 'float':
                return ColumnFieldClass.CUSTOM_FLOAT;
            case 'time':
                return ColumnFieldClass.CUSTOM_TIME;
            case 'duration':
                return ColumnFieldClass.CUSTOM_DURATION;
            case 'dateTime':
                return ColumnFieldClass.CUSTOM_DATE;
            case 'string':
            default:
                return 'custom-cell-left' as ColumnFieldClass;
        }
    }

    public static subtractDayForPeriods(isPeriod: boolean, start_date: string, end_date: string): string {
        if (isPeriod && start_date !== end_date) {
            return moment(end_date, MOMENT_FORMATS).subtract(1, 'day').format(BUSINESS_DATE_FORMAT);
        } else {
            return end_date;
        }
    }

    public static getWarningMessageAboutLimits(
        translations: TranslateService,
        notificationService: NotificationService,
        preferencesService: PreferencesService,
    ): string {
        const limit = preferencesService.numberFormatterForAgGrid(rowLimit);

        notificationService.warning(
            translations.instant('WARNINGS.MAX_ROW_LIMIT_REACHED', { limit }),
            translations.instant('WARNINGS.TOO_MUCH_DATA'),
        );

        return translations.instant('WARNINGS.MAX_ROW_LIMIT_REACHED', { limit });
    }

    public static isValueNotNone(value: string): boolean {
        return !!value && value !== '(None)' && value !== 'N/A';
    }

    public static findUniqueDuplicates(arr: any[]): any[] {
        const counts = arr.reduce(
            (acc, value) => {
                acc[value] = (acc[value] || 0) + 1;
                return acc;
            },
            {} as Record<any, number>,
        );

        return Object.keys(counts).filter((key) => counts[key] > 1);
    }

    public static getHeaderFormattedList(dataMap: Array<Partial<{ name: string }>>): string {
        return dataMap.reduce((acc, item, idx) => (acc += `${item.name}${idx === dataMap.length - 1 ? '' : ', '}`), '');
    }

    public static getVisibleNodeKeysAfterFilterAndSort(
        agGridApi: GridApi,
        key: string,
        filterObj: { [K: string]: string } = {},
    ): string[] {
        const nodeRes = [];
        agGridApi.forEachNodeAfterFilterAndSort((node: RowNode) => {
            if (!node.group && node?.data) {
                const { data } = node;
                if (!data?.[key]) {
                    return;
                }

                const allFiltersPass = Object.keys(filterObj).every((el) => data[el] !== filterObj[el]);
                if (Reflect.ownKeys(filterObj).length && !allFiltersPass) {
                    return;
                }

                nodeRes.push(data[key]);
            }
        });

        return nodeRes;
    }

    public static getHideColumns(agGridOptions: GridOptions, agGridColumnApi: ColumnApi): HiddenColumns[] {
        const selectedColumns = [];
        const hiddenColumns = [];
        const groupColumns = agGridColumnApi.getRowGroupColumns().map((el: Column) => el.getColId());
        agGridOptions.columnApi.getAllColumns().forEach((item: Column) => {
            // group by will hide the column
            if (item.isVisible() || groupColumns.includes(item.getColId())) {
                selectedColumns.push({
                    value: item.getUserProvidedColDef().headerName,
                    id: item.getColId(),
                });
            } else if (!selectedColumns.some((col) => col.id === item.getColId())) {
                hiddenColumns.push({
                    value: item.getUserProvidedColDef().headerName,
                    id: item.getColId(),
                });
            }
        });

        return hiddenColumns;
    }

    public static getOptionsAfterFilterAndSort(agGridApi: GridApi, agGridColumnApi: ColumnApi): CustomQueryParameter {
        const filterModel = agGridApi.getFilterModel();
        let filterObj = [];
        if (Reflect.ownKeys(filterModel).length) {
            filterObj = Reflect.ownKeys(filterModel).map((filed: string) => {
                const setFilter = filterModel[filed]?.filterModels?.find((model) => model.filterType === 'set');
                return { key: filed, value: filterModel[filed]?.values || setFilter?.values || [] };
            });
        }
        const groupSort = [];
        const group = agGridColumnApi.getRowGroupColumns().map((col: Column) => {
            const colId = col.getColId();
            groupSort.push({ colId, sort: 'asc' });
            return colId;
        });

        const excludeGroup = [];

        const sortList = [...groupSort, ...excludeGroup];

        return { group, filter: filterObj, sort: sortList };
    }

    public static prepareCommonHeaders(options, destination = 'grid') {
        const ignoreTitlesList =
            (destination === 'grid' ? options.ignoredGridTitles : options.ignoredChartTitles) || [];
        const ignoreTitlePdfList =
            (destination === 'grid' ? options.ignoredGridPdfTitles : options.ignoredChartPdfTitles) || [];
        const reportId = options.reportId;
        const events = options.leftSideFilters.find((item) => item.name === 'events');
        const sites = options.leftSideFilters.find((item) => item.name === 'sites');
        const calendars = options.leftSideFilters.find((item) => item.name === 'calendars');
        const demo_level = options.demo_level;
        const interval = options.interval;
        const hideInactive = options.hideInactive;
        const etlStatistic = options.etlStatistic;

        const submittedTime = options.submittedTime;
        const rowsCount = options.rowsCount;

        let pdfSubTitles = [];
        let agGridDynamicHeaders = [];
        if (events) {
            let headerText;
            const selectedEvents = options.standAloneFilters?.events
                ? options.eventState.selectedEvents.filter((event) =>
                      options.standAloneFilters.events.find((id) => event.id === id),
                  )
                : events.multiple
                  ? options.eventState.selectedEvents
                  : [options.eventState.selectedEvent];

            if (events.multiple && selectedEvents.length > 1 && !ignoreTitlePdfList.includes('events')) {
                headerText = `${options.translationService.instant('Events')}: ${selectedEvents.length}`;
                pdfSubTitles = [...pdfSubTitles, headerText];
            } else if (
                (!events.multiple && selectedEvents[0]) ||
                (selectedEvents.length === 1 && selectedEvents[0] && !ignoreTitlePdfList.includes('events'))
            ) {
                headerText = `${options.translationService.instant('Event')}: ${selectedEvents[0]?.name}`;
                pdfSubTitles = [...pdfSubTitles, headerText];
            }

            if (!ignoreTitlesList.includes('events')) {
                agGridDynamicHeaders = [
                    ...agGridDynamicHeaders,
                    {
                        id: 'events_header',
                        text: headerText,
                        class: '',
                    },
                ];
            }
        }
        if (sites) {
            const warehouseTypes = uniq(concat(sites.site_type, sites?.business_types || []));

            let headerText;

            const selectedSites = options.standAloneFilters?.sites
                ? options.standAloneFilters.sites === 'All'
                    ? options.siteState.selectedSites
                    : options.siteState.selectedSites.filter((site) =>
                          options.standAloneFilters.sites?.find((id) => site.id === id),
                      )
                : sites.multiple
                  ? options.siteState.selectedSites
                  : [options.siteState.selectedSite];

            const filteredSites = sites.site_type
                ? selectedSites.filter((site) =>
                      some([site.type, site.business_type], (type) => includes(warehouseTypes, type)),
                  )
                : selectedSites;
            if (sites.multiple && filteredSites.length > 1 && !ignoreTitlePdfList.includes('sites')) {
                headerText = `${options.translationService.instant('Sites')}: ${filteredSites.length}`;
                pdfSubTitles = [...pdfSubTitles, headerText];
            } else if (
                (!sites.multiple && selectedSites[0]) ||
                (filteredSites.length === 1 && filteredSites[0] && !ignoreTitlePdfList.includes('sites'))
            ) {
                headerText = `${options.translationService.instant('Site')}: ${!sites.multiple ? selectedSites[0]?.name : filteredSites[0]?.name}`;
                pdfSubTitles = [...pdfSubTitles, headerText];
            }

            if (!ignoreTitlesList.includes('sites')) {
                agGridDynamicHeaders = [
                    ...agGridDynamicHeaders,
                    {
                        id: 'sites_header',
                        text: headerText,
                        class: '',
                    },
                ];
            }
        }
        if (calendars) {
            let headerText = `${options.translationService.instant('BUSINESS_DATE')}: ${options.date}`;
            let pdfDateHeader = options.enLocaleDate
                ? `${options.translationService.instant('BUSINESS_DATE')}: ${options.enLocaleDate}`
                : headerText;

            if (
                events &&
                options.eventState.selectedEvents.length > 0 &&
                reportId === options.eventState.selectedEventsReportId
            ) {
                headerText = `${options.translationService.instant('EVENT_TIME')}: ${AgGridUtils.getEventsDateTimeRange(
                    options.eventState.selectedEvents,
                    options.preferencesService,
                )}`;

                pdfDateHeader = `${options.translationService.instant('EVENT_TIME')}: ${AgGridUtils.getEventsDateTimeRange(
                    options.eventState.selectedEvents,
                    options.preferencesService,
                    'L',
                    'LT',
                    EN_LOCALE,
                )}`;
            }
            if (events && options.eventState.selectedEvent && reportId === options.eventState.selectedEventsReportId) {
                headerText = `${options.translationService.instant('EVENT_TIME')}: ${AgGridUtils.getEventDateTimeRange(
                    options.eventState.selectedEvent,
                    options.preferencesService,
                )}`;

                pdfDateHeader = `${options.translationService.instant('EVENT_TIME')}: ${AgGridUtils.getEventDateTimeRange(
                    options.eventState.selectedEvent,
                    options.preferencesService,
                    EN_LOCALE,
                )}`;
            }

            if (!ignoreTitlesList.includes('calendars')) {
                agGridDynamicHeaders = [
                    ...agGridDynamicHeaders,
                    {
                        id: 'date_header',
                        text: headerText,
                        class: '',
                    },
                ];
            }

            if (!ignoreTitlePdfList.includes('calendars')) {
                pdfSubTitles = [...pdfSubTitles, pdfDateHeader];
            }
        }
        if (demo_level) {
            if (!ignoreTitlesList.includes('demo_level')) {
                agGridDynamicHeaders = [
                    ...agGridDynamicHeaders,
                    {
                        id: 'demo_level',
                        text: `${options.translationService.instant('FILTERS.DEMONSTRATION_LEVEL')}: ${demo_level}`,
                        class: '',
                    },
                ];
            }
            if (!ignoreTitlePdfList.includes('demo_level')) {
                pdfSubTitles = [
                    ...pdfSubTitles,
                    options.translationService.instant('FILTERS.DEMONSTRATION_LEVEL') + ': ' + demo_level,
                ];
            }
        }
        if (interval) {
            if (!ignoreTitlesList.includes('interval')) {
                agGridDynamicHeaders = [
                    ...agGridDynamicHeaders,
                    {
                        id: 'interval',
                        text: `${options.translationService.instant('FILTERS.INTERVAL')}: ${interval}`,
                        class: '',
                    },
                ];
            }
            if (!ignoreTitlePdfList.includes('interval')) {
                pdfSubTitles = [
                    ...pdfSubTitles,
                    options.translationService.instant('FILTERS.INTERVAL') + ': ' + interval,
                ];
            }
        }
        if (hideInactive) {
            if (!ignoreTitlesList.includes('hideInactive')) {
                agGridDynamicHeaders = [
                    ...agGridDynamicHeaders,
                    {
                        id: 'hide_inactive',
                        text: `${options.translationService.instant('FILTERS.HIDE_INACTIVE_ROWS')}: ${hideInactive}`,
                        class: '',
                    },
                ];
            }
            if (!ignoreTitlePdfList.includes('hideInactive')) {
                pdfSubTitles = [
                    ...pdfSubTitles,
                    options.translationService.instant('FILTERS.HIDE_INACTIVE_ROWS') + ': ' + hideInactive,
                ];
            }
        }
        if (rowsCount) {
            if (!ignoreTitlesList.includes('rowsCount')) {
                agGridDynamicHeaders = [
                    ...agGridDynamicHeaders,
                    {
                        id: 'row_count',
                        text: `${options.translationService.instant('COUNT_TOTAL')}: ${options.rowsCount}`,
                        class: '',
                    },
                ];
            }
            if (!ignoreTitlePdfList.includes('rowsCount')) {
                pdfSubTitles = [
                    ...pdfSubTitles,
                    options.translationService.instant('COUNT_TOTAL') + ': ' + options.rowsCount,
                ];
            }
        }
        if (submittedTime) {
            const headerText = `${options.translationService.instant('REPORT_RUN')}: ${options.submittedTime}`;
            const pdfSubmittedTimeHeader = `${options.translationService.instant('REPORT_RUN')}: ${options.enLocaleSubmittedTime ?? options.submittedTime}`;
            pdfSubTitles = [...pdfSubTitles, pdfSubmittedTimeHeader];
            agGridDynamicHeaders = [
                ...agGridDynamicHeaders,
                {
                    id: 'report_run',
                    text: headerText,
                    class: '',
                },
            ];
        }

        if (etlStatistic) {
            const headerText = AgGridUtils.getPreparedEtlStatisticInfo(options, etlStatistic);
            agGridDynamicHeaders = [
                ...agGridDynamicHeaders,
                {
                    id: 'etl_statistic',
                    text: headerText,
                    class: '',
                },
            ];

            if (!ignoreTitlePdfList.includes('etlStatistics')) {
                pdfSubTitles = [...pdfSubTitles, headerText];
            }
        }
        return { pdfSubTitles, agGridDynamicHeaders };
    }

    public static getPreparedEtlStatisticInfo(options, etlStatistic) {
        let etlJobStatus = options.translationService.instant('N/A');
        if (etlStatistic.jobStatus && etlStatistic.needShow && (etlStatistic.jobFinished || etlStatistic.jobStarted)) {
            let date = '';
            if (etlStatistic.jobFinished) {
                date = etlStatistic.jobFinished;
            }
            if (!etlStatistic.jobFinished && etlStatistic.jobStarted) {
                date = etlStatistic.jobStarted;
            }
            const status = options.translationService.instant(
                `ETL_JOB_STATUSES.${etlStatistic.jobStatus.toUpperCase()}`,
            );
            etlJobStatus = `${date}, ${status}`;
        }
        const headerText = `${options.translationService.instant('LAST_UPDATE')}: ${etlJobStatus}`;
        return headerText;
    }

    private static getEventsDateTimeRange(
        events: Array<IEvent>,
        preferencesService: PreferencesService,
        formatDate = 'L',
        formatTime: moment.LongDateFormatKey = 'LT',
        locale?: string,
    ): string {
        const timeLocale = locale ?? preferencesService.timeLocale;
        const min = convertUTCDateTimeByTimeZone(
            moment
                .min(events.map((event) => moment(event.event_days[0].start_date_time, DATE_TIME_DEFAULT_FORMAT)))
                .format(DATE_TIME_DEFAULT_FORMAT),
            preferencesService.timezone,
            DATE_TIME_DEFAULT_FORMAT,
            timeLocale,
        );
        const max = convertUTCDateTimeByTimeZone(
            moment
                .max(events.map((event) => moment(event.event_days[0].end_date_time, DATE_TIME_DEFAULT_FORMAT)))
                .format(DATE_TIME_DEFAULT_FORMAT),
            preferencesService.timezone,
            DATE_TIME_DEFAULT_FORMAT,
            timeLocale,
        );
        const minDate = moment(min, DATE_TIME_DEFAULT_FORMAT).locale(timeLocale).format(formatDate);
        const minTime = moment(min, DATE_TIME_DEFAULT_FORMAT)
            .locale(timeLocale)
            .format(preferencesService.correctMomentTimeFormat(formatTime));

        const maxDate = moment(max, DATE_TIME_DEFAULT_FORMAT).locale(timeLocale).format(formatDate);
        const maxTime = moment(max, DATE_TIME_DEFAULT_FORMAT)
            .locale(timeLocale)
            .format(preferencesService.correctMomentTimeFormat(formatTime));
        return `${minDate} ${minTime} - ${maxDate} ${maxTime}`;
    }

    private static getEventDateTimeRange(
        event: IEvent,
        preferencesService: PreferencesService,
        locale?: string,
    ): string {
        const timeLocale = locale ?? preferencesService.timeLocale;

        const min = convertUTCDateTimeByTimeZone(
            moment
                .min(event.event_days.map((day) => moment(day.start_date_time, DATE_TIME_DEFAULT_FORMAT)))
                .format(DATE_TIME_DEFAULT_FORMAT),
            preferencesService.timezone,
            DATE_TIME_DEFAULT_FORMAT,
            timeLocale,
        );
        const max = convertUTCDateTimeByTimeZone(
            moment
                .max(event.event_days.map((day) => moment(day.end_date_time, DATE_TIME_DEFAULT_FORMAT)))
                .format(DATE_TIME_DEFAULT_FORMAT),
            preferencesService.timezone,
            DATE_TIME_DEFAULT_FORMAT,
            timeLocale,
        );
        return `${min} - ${max}`;
    }

    public static safeDivision(params, dividendField: string, divisorField: string) {
        const dividend = params?.data?.[dividendField] || params?.getValue(dividendField);
        const divisor = params?.data?.[divisorField] || params?.getValue(divisorField);
        return +divisor !== 0 ? parseFloat(dividend) / parseFloat(divisor) : 0;
    }

    public static prepareRowSpecifiedCellParams(
        field: string,
        data: Array<any>,
    ): { value: number | string; data?: Array<any> } {
        return {
            data: data,
            value: get(data, field),
        };
    }
}
